import React, { useEffect } from 'react';
import { Plus, Trash2, ArrowDown } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { TextareaField } from '../../ui/textarea-field';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { newSelect } from '../../ui/newSelect';
import { evaluateShowWhen } from './showWhen';
import { getSurveyIconComponent } from './surveyIcons';
import SurveyNumberStepper from './SurveyNumberStepper';
import WfhDaysPerWeekField, { WORKING_DAYS_QUESTION_KEYS } from './WfhDaysPerWeekField';

/** Matches WfhSurveyPrototype ChoiceCard (rounded-xl, border-2, radio on right). */
const choiceCardClasses = (active) =>
  `w-full text-left rounded-xl border-2 p-3.5 transition-all flex items-start gap-3 ${
    active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
  }`;

const emptyRepeaterRow = (question) => {
  const fields = Array.isArray(question?.fields) ? question.fields : [];
  return fields.reduce((acc, field) => {
    acc[field.key] = '';
    return acc;
  }, {});
};

const fieldValue = (value, fallback = '') => (value == null ? fallback : value);

const NumberInput = ({ question, value, onChange, step = 1 }) => (
  <Input
    type='number'
    value={fieldValue(value)}
    min={question.min}
    max={question.max}
    step={question.step || step}
    placeholder={question.placeholder}
    onChange={(event) => onChange(event.target.value)}
  />
);

const DEFAULT_BOOLEAN_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const ChoiceButtons = ({ options = [], value, onChange, layout = 'stack' }) => {
  const isGrid = layout === 'grid';
  const containerClass = isGrid ? 'grid grid-cols-2 gap-2' : 'space-y-2.5';
  return (
    <div className={containerClass}>
      {options.map((option) => {
        const active = value === option.value;
        const Icon = getSurveyIconComponent(option.icon);
        return (
          <button
            key={String(option.value)}
            type='button'
            className={choiceCardClasses(active)}
            onClick={() => onChange(option.value)}>
            {Icon ? (
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                  active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                <Icon size={16} strokeWidth={1.5} />
              </div>
            ) : null}
            <div className='flex-1 min-w-0 text-left'>
              <div className='text-sm font-semibold'>{option.label}</div>
              {option.description ? (
                <div className='text-[11px] text-muted-foreground mt-0.5'>{option.description}</div>
              ) : null}
            </div>
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                active ? 'border-primary bg-primary' : 'border-input'
              }`}>
              {active ? <span className='w-2 h-2 rounded-full bg-primary-foreground' /> : null}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const RepeaterField = ({ question, value, onChange, answers, stepSlug }) => {
  const rows = Array.isArray(value) ? value : [];
  const maxItems = question.maxItems || 4;
  const minRows =
    stepSlug === 'commuting' ? Math.max(question.min != null ? Number(question.min) : 0, 1) : question.min || 0;
  const isCommutingPro = stepSlug === 'commuting';

  useEffect(() => {
    if (!isCommutingPro) return;
    if (Array.isArray(value) && value.length > 0) return;
    onChange([emptyRepeaterRow(question)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed first leg when opening commuting; avoid re-running on every answers change
  }, [isCommutingPro, question.key]);

  const updateRow = (rowIndex, key, nextValue) => {
    const nextRows = rows.map((row, index) => (index === rowIndex ? { ...row, [key]: nextValue } : row));
    onChange(nextRows);
  };

  const addRow = () => {
    if (rows.length >= maxItems) return;
    onChange([...rows, emptyRepeaterRow(question)]);
  };

  const removeRow = (rowIndex) => {
    if (rows.length <= minRows) return;
    onChange(rows.filter((_, index) => index !== rowIndex));
  };

  if (isCommutingPro) {
    return (
      <div className='space-y-3'>
        {rows.map((row, rowIndex) => (
          <React.Fragment key={`repeater-leg-${rowIndex}`}>
            <div className='rounded-xl border-2 border-border p-3.5 relative'>
              <div className='flex items-center justify-between mb-3'>
                <Badge variant='outline' size='sm'>
                  Leg {rowIndex + 1}
                </Badge>
                {rowIndex > 0 ? (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='h-8 w-8 shrink-0'
                    title='Remove leg'
                    disabled={rows.length <= minRows}
                    onClick={() => removeRow(rowIndex)}>
                    <Trash2 size={14} strokeWidth={1.5} className='text-destructive/70' />
                  </Button>
                ) : (
                  <span className='w-8 shrink-0' aria-hidden />
                )}
              </div>
              <div className='space-y-3'>
                {question.fields.map((subField) => {
                  if (!evaluateShowWhen(subField.showWhen, { ...answers, ...row })) return null;
                  return (
                    <div key={subField.key} className='space-y-1'>
                      <Label className='text-sm font-medium'>{subField.question}</Label>
                      <QuestionField
                        question={subField}
                        answers={{ ...answers, ...row }}
                        value={row[subField.key]}
                        onChange={(nextValue) => updateRow(rowIndex, subField.key, nextValue)}
                        stepSlug={stepSlug}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            {rowIndex < rows.length - 1 ? (
              <div className='flex items-center justify-center'>
                <div className='w-7 h-7 rounded-full bg-muted flex items-center justify-center text-muted-foreground'>
                  <ArrowDown size={14} strokeWidth={1.5} />
                </div>
              </div>
            ) : null}
          </React.Fragment>
        ))}
        {rows.length < maxItems ? (
          <Button type='button' variant='outline' className='w-full' onClick={addRow}>
            <Plus size={14} strokeWidth={1.5} />
            Add another leg ({rows.length} of {maxItems})
          </Button>
        ) : (
          <div className='text-center text-xs text-muted-foreground'>Maximum {maxItems} legs — covers most multi-modal commutes.</div>
        )}
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      {rows.map((row, rowIndex) => (
        <div key={`repeater-${rowIndex}`} className='border border-border rounded-lg p-3 space-y-3'>
          <div className='flex items-center justify-between'>
            <div className='text-sm font-semibold'>Leg {rowIndex + 1}</div>
            <Button type='button' variant='ghost' size='sm' disabled={rows.length <= minRows} onClick={() => removeRow(rowIndex)}>
              Remove
            </Button>
          </div>
          {question.fields.map((subField) => {
            if (!evaluateShowWhen(subField.showWhen, { ...answers, ...row })) return null;
            return (
              <div key={subField.key} className='space-y-1'>
                <Label>{subField.question}</Label>
                <QuestionField
                  question={subField}
                  answers={{ ...answers, ...row }}
                  value={row[subField.key]}
                  onChange={(nextValue) => updateRow(rowIndex, subField.key, nextValue)}
                  stepSlug={stepSlug}
                />
              </div>
            );
          })}
        </div>
      ))}
      <Button type='button' variant='outline' disabled={rows.length >= maxItems} onClick={addRow}>
        Add commute leg
      </Button>
    </div>
  );
};

const renderIntegerField = (question, value, onChange, answers) => {
  if (question.key === 'wfh_days_per_week') {
    return <WfhDaysPerWeekField value={value} onChange={onChange} answers={answers} />;
  }

  if (WORKING_DAYS_QUESTION_KEYS.includes(question.key)) {
    return <SurveyNumberStepper value={value} onChange={onChange} min={1} max={7} />;
  }

  const Icon = getSurveyIconComponent(question.icon);
  if (Icon) {
    const imin = question.min != null ? Number(question.min) : 0;
    const imax = question.max != null ? Number(question.max) : 999;
    const compactRange = Number.isFinite(imax) && Number.isFinite(imin) && imax - imin <= 50;
    return (
      <div className='flex items-center gap-3 rounded-xl border border-border p-3'>
        <div className='w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0'>
          <Icon size={18} strokeWidth={1.5} />
        </div>
        <div className='flex-1 min-w-0'>
          {compactRange ? (
            <SurveyNumberStepper value={value} onChange={onChange} min={imin} max={imax} />
          ) : (
            <NumberInput question={question} value={value} onChange={onChange} step={1} />
          )}
        </div>
      </div>
    );
  }

  return <NumberInput question={question} value={value} onChange={onChange} step={1} />;
};

const QuestionField = ({ question, answers = {}, value, onChange, stepSlug }) => {
  const type = question?.type;
  const NewSelect = newSelect;

  if (type === 'select') {
    return (
      <NewSelect
        options={(question.options || []).map((option) => ({
          value: option.value,
          label: option.label,
          ...(option.icon ? { icon: option.icon } : {}),
        }))}
        value={fieldValue(value, null)}
        onChange={onChange}
        placeholder={question.placeholder || 'Select an option'}
      />
    );
  }

  if (type === 'single_choice' || type === 'boolean') {
    const rawOptions = question.options?.length ? question.options : type === 'boolean' ? DEFAULT_BOOLEAN_OPTIONS : [];
    const useRowLayout =
      type === 'boolean' ||
      (type === 'single_choice' && rawOptions.length === 2 && question.stepSlug === 'lighting');
    return (
      <ChoiceButtons
        options={rawOptions}
        value={value}
        onChange={onChange}
        layout={useRowLayout ? 'grid' : 'stack'}
      />
    );
  }

  if (type === 'integer') return renderIntegerField(question, value, onChange, answers);

  if (type === 'decimal') {
    const Icon = getSurveyIconComponent(question.icon);
    if (Icon) {
      return (
        <div className='flex items-center gap-3 rounded-xl border border-border p-3'>
          <div className='w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0'>
            <Icon size={18} strokeWidth={1.5} />
          </div>
          <div className='flex-1 min-w-0'>
            <NumberInput question={question} value={value} onChange={onChange} step={question.step || 0.1} />
          </div>
        </div>
      );
    }
    return <NumberInput question={question} value={value} onChange={onChange} step={question.step || 0.1} />;
  }

  if (type === 'text') {
    return (
      <TextareaField
        rows={3}
        value={fieldValue(value)}
        placeholder={question.placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (type === 'weekly_toggle') {
    const currentValue = Array.isArray(value) ? value : [];
    const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const toggleDay = (day) => {
      if (currentValue.includes(day)) onChange(currentValue.filter((item) => item !== day));
      else onChange([...currentValue, day]);
    };

    const dayTileClasses = (active) =>
      `w-full rounded-lg border p-3 text-center text-sm transition-colors ${
        active ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40'
      }`;

    return (
      <div className='grid grid-cols-7 gap-2'>
        {dayLabels.map((day) => (
          <button
            key={day}
            type='button'
            className={dayTileClasses(currentValue.includes(day))}
            onClick={() => toggleDay(day)}>
            {day}
          </button>
        ))}
      </div>
    );
  }

  if (type === 'repeater') {
    return <RepeaterField question={question} answers={answers} value={value} onChange={onChange} stepSlug={stepSlug} />;
  }

  return (
    <Input
      value={fieldValue(value)}
      placeholder={question.placeholder || ''}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

export default QuestionField;
